/**
 * usePassageHighlights — controller for the passage highlighter pen.
 *
 * Reads through a module-level store (see passageHighlightStore.ts) rather than useState,
 * because PassagePanel and PassageCard are both mounted at once and must share one state.
 *
 * Pass a falsy sessionId to get an inert controller. That is how the parent's read-only
 * session view, and any host without a session, opt out: no subscription, no persistence,
 * no toolbar.
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import * as store from '@/lib/passageHighlightStore';
import {
  addHighlight,
  bridgeWhitespaceGaps,
  eraseAt as eraseRangeAt,
  snapRange,
  tokenize,
  type HighlightColor,
  type HighlightRange,
  type Token,
} from '@/lib/passageHighlights';

export interface PassageHighlightController {
  /** Sorted, merged, non-overlapping. */
  ranges: HighlightRange[];
  armedColor: HighlightColor | null;
  /** Tokenized passage — the render layer needs it for word spans. */
  tokens: Token[];
  /** True when this controller is wired to a session and may mutate state. */
  enabled: boolean;
  /** Arm a pen, or pass null to put it down. */
  setArmedColor: (color: HighlightColor | null) => void;
  /** Highlight from the word at offset `a` to the word at offset `b`, inclusive. */
  highlightRange: (a: number, b: number) => void;
  /** Erase the whole contiguous highlight sitting under `offset`. */
  eraseAt: (offset: number) => void;
  clearAll: () => void;
}

export function usePassageHighlights(
  sessionId: string | undefined,
  passage: string
): PassageHighlightController {
  const key = sessionId ?? '';
  const enabled = key !== '' && passage.length > 0;

  const subscribeToSession = useCallback(
    (listener: () => void) => store.subscribe(key, listener),
    [key]
  );
  const readSnapshot = useCallback(() => store.getSnapshot(key), [key]);

  const snapshot = useSyncExternalStore(subscribeToSession, readSnapshot, readSnapshot);

  const tokens = useMemo(() => tokenize(passage), [passage]);

  useEffect(() => {
    store.hydrate(key, passage.length);
  }, [key, passage.length]);

  const setArmedColor = useCallback(
    (color: HighlightColor | null) => store.setArmedColor(key, color),
    [key]
  );

  const highlightRange = useCallback(
    (a: number, b: number) => {
      const armed = store.getSnapshot(key).armedColor;
      if (!armed) return;

      const snapped = snapRange(tokens, a, b);
      if (!snapped) return; // Endpoint was a line-number prefix or a stale offset.

      const current = store.getSnapshot(key).ranges;
      const next = addHighlight(current, { ...snapped, color: armed });
      // Two highlights separated only by a space must read as one continuous stroke.
      store.setRanges(key, bridgeWhitespaceGaps(next, tokens), passage.length);
    },
    [key, tokens, passage.length]
  );

  const eraseAt = useCallback(
    (offset: number) => {
      const current = store.getSnapshot(key).ranges;
      const next = eraseRangeAt(current, offset);
      if (next !== current) store.setRanges(key, next, passage.length);
    },
    [key, passage.length]
  );

  const clearAll = useCallback(() => store.setRanges(key, [], passage.length), [key, passage.length]);

  return {
    ranges: snapshot.ranges,
    armedColor: snapshot.armedColor,
    tokens,
    enabled,
    setArmedColor,
    highlightRange,
    eraseAt,
    clearAll,
  };
}
